import { join } from "node:path";
import { getPackageDir, isBunBinary, isBundledBuild } from "../../config.js";
import { createModuleRequire } from "../../utils/module-require.ts";

let virtualModules: Record<string, object> | null = null;
let virtualModulesPromise: Promise<Record<string, object>> | null = null;

type HostModuleRequire = (specifier: string) => object;

function loadOptionalAtomicNatives(requireFromHost: HostModuleRequire, specifier: string): object | undefined {
	try {
		return requireFromHost(specifier);
	} catch {
		// Keep a missing or unloadable platform binding proportional: only an
		// extension that imports atomic-natives should fail, not every extension
		// waiting for the shared host-module map.
		return undefined;
	}
}

export async function loadVirtualModules(): Promise<Record<string, object>> {
	// Keep the native binding external to both the app sidecar and extension
	// bundles while sharing its process-global control plane through the bridge.
	// If the platform package is unavailable, omit only this registration so
	// unrelated builtins can still load.
	const requireFromHost = createModuleRequire(import.meta.url);
	const atomicNatives = loadOptionalAtomicNatives(
		requireFromHost,
		isBunBinary
			? join(getPackageDir(), "node_modules", "@bastani", "atomic-natives", "native", "index.js")
			: "@bastani/atomic-natives",
	);
	const [
		typebox,
		typeboxCompile,
		typeboxValue,
		piAgentCore,
		piTui,
		piTuiLayout,
		piTuiLayoutNode,
		piAi,
		piAiOauth,
		piAiModels,
		piAiCloudflareGatewayBinding,
		piAiProviderEnv,
		piAiOauthPage,
		properLockfile,
		piCodingAgent,
		storage,
	] = await Promise.all([
		import("typebox"),
		import("typebox/compile"),
		import("typebox/value"),
		import("@earendil-works/pi-agent-core"),
		import("@earendil-works/pi-tui"),
		import("@earendil-works/pi-tui/dist/layout.js"),
		import("@earendil-works/pi-tui/dist/layout-node.js"),
		// pi 0.80.2: the old global pi-ai API moved off the root entrypoint onto
		// `/compat` (a strict superset). Extensions still use the root specifier.
		import("@bastani/pi-ai/compat"),
		import("@bastani/pi-ai/oauth"),
		import("@bastani/pi-ai/models"),
		import("@bastani/pi-ai/api/cloudflare-gateway-binding"),
		import("@bastani/pi-ai/utils/provider-env"),
		import("@bastani/pi-ai/utils/oauth-page"),
		// Keep proper-lockfile in the compiled host so extensions share its live
		// CommonJS module state instead of evaluating it through jiti.
		import("proper-lockfile"),
		// loader.ts exports are not re-exported from index.ts, avoiding a cycle.
		import("../../index.ts"),
		import("../storage/index.js"),
	]);

	return {
		typebox,
		"typebox/compile": typeboxCompile,
		"typebox/value": typeboxValue,
		"@sinclair/typebox": typebox,
		"@sinclair/typebox/compile": typeboxCompile,
		"@sinclair/typebox/value": typeboxValue,
		"@earendil-works/pi-agent-core": piAgentCore,
		"@earendil-works/pi-tui": piTui,
		"@earendil-works/pi-tui/dist/layout.js": piTuiLayout,
		"@earendil-works/pi-tui/dist/layout-node.js": piTuiLayoutNode,
		"@bastani/pi-ai": piAi,
		"@bastani/pi-ai/compat": piAi,
		"@bastani/pi-ai/oauth": piAiOauth,
		"@bastani/pi-ai/models": piAiModels,
		"@bastani/pi-ai/api/cloudflare-gateway-binding": piAiCloudflareGatewayBinding,
		"@bastani/pi-ai/utils/provider-env": piAiProviderEnv,
		"@bastani/pi-ai/utils/oauth-page": piAiOauthPage,
		"@earendil-works/pi-ai": piAi,
		"@earendil-works/pi-ai/compat": piAi,
		"@earendil-works/pi-ai/oauth": piAiOauth,
		"@earendil-works/pi-ai/models": piAiModels,
		"@earendil-works/pi-ai/api/cloudflare-gateway-binding": piAiCloudflareGatewayBinding,
		"proper-lockfile": properLockfile,
		...(atomicNatives ? { "@bastani/atomic-natives": atomicNatives } : {}),
		"@bastani/atomic": piCodingAgent,
		"@bastani/atomic/storage": storage,
		"@earendil-works/pi-coding-agent": piCodingAgent,
		"@mariozechner/pi-agent-core": piAgentCore,
		"@mariozechner/pi-tui": piTui,
		"@mariozechner/pi-tui/dist/layout.js": piTuiLayout,
		"@mariozechner/pi-ai": piAi,
		"@mariozechner/pi-ai/compat": piAi,
		"@mariozechner/pi-ai/oauth": piAiOauth,
		"@mariozechner/pi-ai/models": piAiModels,
		"@mariozechner/pi-ai/api/cloudflare-gateway-binding": piAiCloudflareGatewayBinding,
	};
}

export const loaderHostModulesTestHooks = { loadOptionalAtomicNatives };

/** Live host modules shared with single-file builds and transformed extension reloads. */
export async function getVirtualModules(): Promise<Record<string, object>> {
	if (virtualModules) return virtualModules;
	virtualModulesPromise ??= loadVirtualModules().then(
		(modules) => {
			virtualModules = modules;
			return modules;
		},
		(error: Error) => {
			virtualModulesPromise = null;
			throw error;
		},
	);
	return virtualModulesPromise;
}

export async function getWorkflowHostModules(): Promise<
	Record<"@bastani/atomic" | "@bastani/atomic/storage" | "@bastani/pi-ai" | "@bastani/pi-ai/providers/all", object>
> {
	const [atomic, storage, piAi, providers] = await Promise.all([
		import("../../index.ts"),
		import("../storage/index.js"),
		import("@bastani/pi-ai/compat"),
		import("@bastani/pi-ai/providers/all"),
	]);
	return {
		"@bastani/atomic": atomic,
		"@bastani/atomic/storage": storage,
		"@bastani/pi-ai": piAi,
		"@bastani/pi-ai/providers/all": providers,
	};
}

/**
 * Live host modules for storage adapter modules. Outside single-file builds `@bastani/atomic` resolves lazily through
 * the extension aliases instead, so a process that needs only storage, such as the Intercom broker, never evaluates
 * the whole host package.
 */
export async function getStorageAdapterHostModules(): Promise<Record<string, object>> {
	const storage = await import("../storage/index.js");
	if (!(isBunBinary || isBundledBuild)) return { "@bastani/atomic/storage": storage };
	return { "@bastani/atomic": await import("../../index.ts"), "@bastani/atomic/storage": storage };
}
